import React from 'react';
import immutable from 'immutable';
import Checkbox from 'material-ui/Checkbox';
import Divider from 'material-ui/Divider';
import Toggle from 'material-ui/Toggle';
import ConstValues from 'utils/const';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import VotingRadioButtonsSearchFieldActions, {
  VotingRadioButtonsSearchFieldValue,
} from 'actions/search/VotingRadioButtonsSearchFieldActions';
import VotingRadioButtonsGroup from 'components/pure/search/voting/VotingRadioButtonsGroup';
import classNames from 'classnames';
import 'styles/search/VotingRadioButtonsSearchField.styl';
import {distanceBetweenElementAndBottomOfWindow} from 'stores/distanceToBottom';
import {SELECTED_KEYS} from 'components/pure/search/SELECTED_KEYS';
import cn from 'classnames';
import {getSearchFieldClass} from 'components/stateful/search/getSearchFieldClass';

// first-level keys are radio-button keys that; once selected, directly updates the search results
const FIRST_LEVEL_KEYS = [SELECTED_KEYS.i_rated_good, SELECTED_KEYS.i_rated_bad];
// second-level keys are radio-buttons nested inside an outer group; once selected, updates the search results
const SECOND_LEVEL_KEYS = [SELECTED_KEYS.others_rated_good, SELECTED_KEYS.others_rated_bad];

/**
 * Render a radio buttons, nested radio buttons dropdown search field for "Voting"
 */
class VotingRadioButtonsSearchField extends React.PureComponent {
  constructor() {
    super();
    this.state = {
      selectedKey: SELECTED_KEYS.none,
    };
    this.radioButtons = immutable.List([
      {
        className: 'voting-search-field-radio-button',
        name: 'Calls I rated as good',
        key: SELECTED_KEYS.i_rated_good,
      },
      {
        className: 'voting-search-field-radio-button',
        name: 'Calls I rated as bad',
        key: SELECTED_KEYS.i_rated_bad,
      },
      {
        className: 'voting-search-field-radio-button',
        name: 'Calls others rated as good',
        key: SELECTED_KEYS.others_rated_good,
      },
      {
        className: 'voting-search-field-radio-button',
        name: 'Calls others rated as bad',
        key: SELECTED_KEYS.others_rated_bad,
      },
    ]);
    this.updateDimensions = this.updateDimensions.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
    window.addEventListener('scroll', this.updateDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
    window.removeEventListener('scroll', this.updateDimensions);
    this._clearUpdateDimensionsTimer();
  }

  _clearUpdateDimensionsTimer() {
    if (this.updateDimensionsTimer) {
      window.clearTimeout(this.updateDimensionsTimer);
    }
  }

  updateDimensions() {
    this._clearUpdateDimensionsTimer();
    this.updateDimensionsTimer = setTimeout(
      () => this._updateMultipleSelectFieldDropdownDirectionIfNecessary(),
      100
    );
  }

  _updateMultipleSelectFieldDropdownDirectionIfNecessary() {
    if (this._multipleSelectField) {
      const {up, down} = ConstValues.reactSelectize.dropdownDirection;
      const distance = distanceBetweenElementAndBottomOfWindow(this._multipleSelectField);
      if (distance < ConstValues.reactSelectize.dropdownHeight) {
        if (this.state.multipleSelectFieldDropdownDirection !== up) {
          this.setState({multipleSelectFieldDropdownDirection: up});
        }
      } else if (this.state.multipleSelectFieldDropdownDirection !== down) {
        this.setState({multipleSelectFieldDropdownDirection: down});
      }
    }
  }

  /**
   * Callback to handle the toggle of the voting search widget
   * @param _ toggle event object
   * @param toggled boolean indicating whether the search widget is toggled or not
   */
  onToggleSearchField = (_, toggled) => {
    SearchFieldsActions.updateSearchWidgetToggleState(
      ConstValues.radioButtonsWidgetTypeForVoting,
      toggled,
      this.props.searchField
    );
    if (!toggled) {
      this.setState({selectedKey: SELECTED_KEYS.none});
      VotingRadioButtonsSearchFieldActions.resetVoting();
    } else {
      this.props.updateLastToggledOnSearchWidget(this.props.searchField.name);
    }
    setTimeout(() => this.updateDimensions(), 100);
  };

  /**
   * Render a nested toggle-box/radio-button based widgets plus dropdowns for voting searches
   */
  render() {
    const classes = cn(
      'toggle-radio-buttons-search-widget',
      getSearchFieldClass(this.props.searchField.name)
    );
    return (
      <div className={classes}>
        <Toggle
          label={this.props.searchField.name}
          defaultToggled={false}
          onToggle={this.onToggleSearchField}
        />
        <Divider className={'toggle-radio-buttons-divider'} />
        {this.props.toggled ? <div>{this.renderVotingRadioButtonsFilter()}</div> : null}
      </div>
    );
  }

  checkboxShouldBeChecked(assignedKey) {
    return assignedKey === this.state.selectedKey;
  }

  /** Event callback that's called when a checkbox is marked/checked */
  onCheckboxCheck = (event, checked) => {
    if (checked) {
      const eventSelectedKey = event.target.dataset.key;
      this.setState({selectedKey: eventSelectedKey});
      if (FIRST_LEVEL_KEYS.includes(eventSelectedKey)) {
        VotingRadioButtonsSearchFieldActions.changeVoting(
          VotingRadioButtonsSearchFieldValue({key: eventSelectedKey})
        );
      } else if (SECOND_LEVEL_KEYS.includes(eventSelectedKey)) {
        // Update the voting-level to second and a follow-up call of onChangeSecondLevelValue would update the search
        // values for real
        VotingRadioButtonsSearchFieldActions.changeVotingLevel(
          VotingRadioButtonsSearchFieldValue({key: eventSelectedKey})
        );
      }
    } else {
      this.setState({selectedKey: SELECTED_KEYS.none});
    }
  };

  /** Event callback that's called when a checkbox inside the component VotingRadioButtonsGroup is selected */
  onChangeSecondLevelValue = secondLevelValue => {
    VotingRadioButtonsSearchFieldActions.changeVoting(
      VotingRadioButtonsSearchFieldValue({value: secondLevelValue.value})
    );
  };

  renderVotingRadioButtonsFilter() {
    const style = {fontSize: '0.9em', marginBottom: '5px', marginTop: '5px'};
    return (
      <div className="voting-search-widget">
        {this.props.store.error ? this._renderErrorMessageDiv(this.props.store.error) : null}
        <Checkbox
          onCheck={this.onCheckboxCheck}
          data-key={SELECTED_KEYS.i_rated_good}
          style={style}
          label={'Calls I rated as good'}
          checked={this.checkboxShouldBeChecked(SELECTED_KEYS.i_rated_good)}
        />
        <Checkbox
          onCheck={this.onCheckboxCheck}
          data-key={SELECTED_KEYS.i_rated_bad}
          style={style}
          label={'Calls I rated as bad'}
          checked={this.checkboxShouldBeChecked(SELECTED_KEYS.i_rated_bad)}
        />
        <VotingRadioButtonsGroup
          onCheck={this.onCheckboxCheck}
          dataKey={SELECTED_KEYS.others_rated_good}
          onChangeSecondLevelValue={this.onChangeSecondLevelValue}
          style={style}
          label={'Calls others rated as good'}
          checked={this.checkboxShouldBeChecked(SELECTED_KEYS.others_rated_good)}
          store={this.props.store}
        />
        <VotingRadioButtonsGroup
          onCheck={this.onCheckboxCheck}
          dataKey={SELECTED_KEYS.others_rated_bad}
          onChangeSecondLevelValue={this.onChangeSecondLevelValue}
          style={style}
          label={'Calls others rated as bad'}
          checked={this.checkboxShouldBeChecked(SELECTED_KEYS.others_rated_bad)}
          store={this.props.store}
        />
      </div>
    );
  }

  _renderErrorMessageDiv(error, extraClassname = null) {
    const errorDivClassname = classNames('error-tip', extraClassname);
    const message = `${error.response.statusText}: ${error.response.data.error}`;
    return (
      <div className={errorDivClassname}>
        <span>
          <i className="material-icons">error_outline</i>
          &nbsp;&nbsp; {message}
        </span>
      </div>
    );
  }
}

export default VotingRadioButtonsSearchField;
